import { useState, useEffect, useRef } from "react";
import { dismissModal } from "@/utils/modalClose";
import { TOAST_CLOSE_BUTTONS } from "@/utils/toast";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  IonHeader, IonToolbar, IonTitle, IonButtons,
  IonInput, IonSelect, IonSelectOption,
  IonButton, IonIcon, IonSpinner, IonTextarea, IonToast,
  IonSegment, IonSegmentButton, IonLabel, IonCheckbox, IonNote, IonItem, IonToggle,
} from "@ionic/react";
import {
  cloudDownloadOutline, cloudUploadOutline, documentTextOutline, eyeOutline, trashOutline, addOutline,
  closeOutline, chevronBackOutline, chevronForwardOutline, sparklesOutline,
  refreshOutline, personOutline, briefcaseOutline, schoolOutline,
  bulbOutline, searchOutline,
} from "ionicons/icons";
import { generateAndDownloadResume } from "@/utils/resumeGenerator";
import { generateResumePreview } from "@/utils/resumePreviewGenerator";
import { isNative, nativePost, getStripeOrigin } from "@/utils/nativeHttp"; // eslint-disable-line no-unused-vars
import { useDisabledGenerators } from "@/utils/generatorAvailability";
import CheckoutWithPaywall from "@/components/CheckoutWithPaywall";
import { deliverDownload } from "@/utils/pendingDownload";
import { emitAppLive } from "@/utils/appLive";
import { addReadyNotification } from "@/utils/appNotifications";
import UploadDropzone from "@/components/UploadDropzone";
import { FLOAT } from "@/components/SettingsRows";

import FreshOverlay from "@/components/FreshOverlay";
import { hasFreeDownloads, walletBalance } from "@/utils/freeDownloads";
import { useDocPrice } from "@/utils/docPrices";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const STORAGE_KEY = "resumeBuilderFormData";
const GENERATED_KEY = "resumeBuilderGenerated";

const cardStyle = {
  backgroundColor: "var(--ion-card-background)",
  borderRadius: 8,
  boxShadow: "rgba(0,0,0,0.14) 0px 2px 12px",
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 14,
};
const headingStyle = { fontWeight: 700, fontSize: "0.95rem", color: "var(--ion-text-color)" };
const labelStyle = { fontSize: "0.75rem", color: "var(--ion-color-medium)", marginBottom: 4, display: "block" };
// Inputs match the paystub modal: plain outline + floating label, no overrides
const inputStyle = {};

const TEMPLATES = [
  { value: "ats",     label: "ATS Optimized",      desc: "Passes ATS scanners",      color: "#2563eb" },
  { value: "modern",  label: "Modern Professional", desc: "Contemporary with accents", color: "#34d399" },
  { value: "classic", label: "Classic Executive",   desc: "Formal, senior roles",     color: "#1e293b" },
];

const FONTS = [
  { value: "Calibri",         label: "Calibri" },
  { value: "Arial",           label: "Arial" },
  { value: "Montserrat",      label: "Montserrat" },
  { value: "Helvetica",       label: "Helvetica" },
  { value: "Times New Roman", label: "Times New Roman" },
];

const STEPS = [
  { id: 1, label: "Personal",   icon: personOutline },
  { id: 2, label: "Experience", icon: briefcaseOutline },
  { id: 3, label: "Education",  icon: schoolOutline },
  { id: 4, label: "Skills",     icon: bulbOutline },
  { id: 5, label: "Target Job", icon: searchOutline },
  { id: 6, label: "Generate",   icon: sparklesOutline },
];

const makeWork = () => ({
  id: `work_${Date.now()}_${Math.random().toString(36).slice(2)}`,
  company: "", position: "", location: "",
  startDate: "", endDate: "", current: false,
  responsibilities: [""],
});

const makeEdu = () => ({
  id: `edu_${Date.now()}_${Math.random().toString(36).slice(2)}`,
  institution: "", degree: "", field: "", graduationDate: "", gpa: "",
});

const defaultFormData = {
  template: "ats", font: "Calibri", sectionLayout: "standard", onePage: false,
  personalInfo: { fullName: "", email: "", phone: "", location: "", linkedin: "", website: "" },
  workExperience: [makeWork()],
  education: [makeEdu()],
  skills: [""],
  targetJobTitle: "", jobDescription: "", jobUrl: "",
};

export default function AppResumeBuilder({ isOpen, onClose: onCloseNow, embedTemplate = null, embedTemplateName = "" }) {
  const onClose = (e) => dismissModal(onCloseNow, e); // animated exit, then the real close

  // Embedded in a business's own category: the builder opens on their design
  // (the only style offered), and that choice never overwrites the style the
  // normal builder remembers.
  const embed = !!embedTemplate;
  // A business's own design is part of its plan: never priced, never charged
  // to the wallet (the server still counts the download against the plan).
  const ownDesign = embed && String(embedTemplate).startsWith("usercustom:");
  const rememberedTemplate = useRef("ats");

  const [formData, setFormData] = useState(() => {
    let base = defaultFormData;
    try { const s = localStorage.getItem(STORAGE_KEY); if (s) base = { ...defaultFormData, ...JSON.parse(s) }; }
    catch {}
    rememberedTemplate.current = base.template || "ats";
    return embed ? { ...base, template: embedTemplate } : base;
  });

  const [generatedResume, setGeneratedResume] = useState(() => {
    try { const s = localStorage.getItem(GENERATED_KEY); return s ? JSON.parse(s) : null; }
    catch { return null; }
  });

  const navigate = useNavigate();
  const [currentStep, setCurrentStep]                       = useState(1);
  const [customTemplates, setCustomTemplates]               = useState([]);
  const [userTemplates, setUserTemplates]                   = useState([]); // the business's own designs
  const [user, setUser]                                     = useState(null);
  const [hasActiveSubscription, setHasActiveSubscription]   = useState(false);
  const [isGenerating, setIsGenerating]                     = useState(false);
  const [isGeneratingBullets, setIsGeneratingBullets]       = useState(null);
  const [isScrapingJob, setIsScrapingJob]                   = useState(false);
  const [jobUrlError, setJobUrlError]                       = useState("");
  const [isProcessing, setIsProcessing]                     = useState(false);
  const [pdfPreview, setPdfPreview]                         = useState(null);
  const [isGeneratingPreview, setIsGeneratingPreview]       = useState(false);
  const [showPreview, setShowPreview]                       = useState(false);
  const [toastOpen, setToastOpen]                           = useState(false);
  const [toastMessage, setToastMessage]                     = useState("");
  const [toastColor, setToastColor]                         = useState("success");
  const [paymentOpen, setPaymentOpen]                       = useState(false);
  const [couponCode, setCouponCode]                         = useState("");
  const [isValidatingCoupon, setIsValidatingCoupon]         = useState(false);
  const [couponError, setCouponError]                       = useState("");
  const [appliedDiscount, setAppliedDiscount]               = useState(null);

  const showToast = (msg, color = "success") => { setToastMessage(msg); setToastColor(color); setToastOpen(true); };
  const setField = (f, v) => setFormData(p => ({ ...p, [f]: v }));

  // ── Upload + parse an existing resume to auto-fill (same endpoint as web) ──
  const resumeFileRef = useRef(null);
  const [isParsingResume, setIsParsingResume] = useState(false);
  const [uploadedResumeName, setUploadedResumeName] = useState(null);

  const handleResumeUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const validTypes = ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    if (!validTypes.includes(file.type) && !/\.(pdf|docx)$/i.test(file.name)) {
      showToast("Please upload a PDF or DOCX file", "danger");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast("File size must be less than 10MB", "danger");
      return;
    }
    setIsParsingResume(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${BACKEND_URL}/api/parse-resume`, { method: "POST", body: fd });
      if (!res.ok) {
        let msg = "Failed to parse resume";
        try { msg = (await res.json()).detail || msg; } catch {}
        throw new Error(msg);
      }
      const result = await res.json();
      if (result.success && result.data) {
        const parsed = result.data;
        setFormData(prev => ({
          ...prev,
          personalInfo: {
            fullName: parsed.personalInfo?.fullName || prev.personalInfo.fullName,
            email: parsed.personalInfo?.email || prev.personalInfo.email,
            phone: parsed.personalInfo?.phone || prev.personalInfo.phone,
            location: parsed.personalInfo?.location || prev.personalInfo.location,
            linkedin: parsed.personalInfo?.linkedin || prev.personalInfo.linkedin,
            website: parsed.personalInfo?.website || prev.personalInfo.website,
          },
          workExperience: parsed.workExperience?.length > 0
            ? parsed.workExperience.map((exp, i) => ({
                id: `work_parsed_${Date.now()}_${i}`,
                company: exp.company || "", position: exp.position || "", location: exp.location || "",
                startDate: exp.startDate || "", endDate: exp.endDate || "", current: exp.current || false,
                responsibilities: exp.responsibilities?.length ? exp.responsibilities : [""],
              }))
            : prev.workExperience,
          education: parsed.education?.length > 0
            ? parsed.education.map((edu, i) => ({
                id: `edu_parsed_${Date.now()}_${i}`,
                institution: edu.institution || "", degree: edu.degree || "", field: edu.field || "",
                graduationDate: edu.graduationDate || "", gpa: edu.gpa || "",
              }))
            : prev.education,
          skills: parsed.skills?.length > 0 ? parsed.skills : prev.skills,
        }));
        setUploadedResumeName(file.name);
        showToast("Resume parsed. Your details have been filled in.", "success");
      } else {
        showToast("Could not extract information from the resume", "danger");
      }
    } catch (err) {
      showToast(err.message || "Failed to parse resume", "danger");
    } finally {
      setIsParsingResume(false);
    }
  };

  useEffect(() => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(embed ? { ...formData, template: rememberedTemplate.current } : formData)); } catch {} }, [formData]); // eslint-disable-line
  useEffect(() => { try { if (generatedResume) localStorage.setItem(GENERATED_KEY, JSON.stringify(generatedResume)); } catch {} }, [generatedResume]);
  useEffect(() => {
    checkSub();
    const f = () => checkSub();
    window.addEventListener("ms:entitlements-changed", f); // the native paywall just credited the account
    return () => window.removeEventListener("ms:entitlements-changed", f);
  }, []); // eslint-disable-line

  // Admin-published custom resume templates (from the doc template editor)
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/doc-templates?documentType=resume`)
      .then((r) => r.json())
      .then((d) => { if (d.success) setCustomTemplates(d.templates || []); })
      .catch(() => {});
    const token = localStorage.getItem("userToken");
    if (!token) return;
    fetch(`${BACKEND_URL}/api/user/doc-templates?baseDocType=resume&published=1`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => { if (d && d.success && d.hasAccess) setUserTemplates(d.templates || []); })
      .catch(() => {});
  }, []);

  // Admin can disable built-in styles or custom templates from Site Settings
  const disabledGenerators = useDisabledGenerators();
  const templateOptions = embed ? [
    { value: embedTemplate, label: embedTemplateName || "Your design", desc: "Your business's design", color: "#34d399" },
  ] : [
    ...TEMPLATES.filter((t) => !disabledGenerators.has(`resume-${t.value}`)),
    ...customTemplates
      .filter((t) => !disabledGenerators.has(`custom:${t.id}`))
      .map((t) => ({ value: `custom:${t.id}`, label: t.name, desc: t.description || "Custom design", color: t.badgeColor || "#34d399" })),
    ...userTemplates.map((t) => ({ value: `usercustom:${t.id}`, label: t.name, desc: t.description || "Your business's design", color: t.badgeColor || "#34d399" })),
  ];

  const checkSub = async () => {
    const token = localStorage.getItem("userToken");
    const userInfo = localStorage.getItem("userInfo");
    if (!token || !userInfo) return;
    try {
      const ud = JSON.parse(userInfo);
      setUser(ud);
      if (hasFreeDownloads(ud))
        setHasActiveSubscription(true);
      const res = await fetch(`${BACKEND_URL}/api/user/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        if (d.success && d.user) {
          setUser(d.user); localStorage.setItem("userInfo", JSON.stringify(d.user));
          setHasActiveSubscription(hasFreeDownloads(d.user));
        }
      }
    } catch {}
  };

  // Preview whenever generated resume or style changes
  useEffect(() => {
    if (!generatedResume) return;
    const t = setTimeout(() => triggerPreview(generatedResume), 600);
    return () => clearTimeout(t);
  }, [generatedResume, formData.template, formData.font, formData.sectionLayout, formData.onePage]); // eslint-disable-line

  const triggerPreview = async resume => {
    if (!resume) return;
    setIsGeneratingPreview(true);
    try {
      const url = await generateResumePreview({ ...resume, template: formData.template, font: formData.font, sectionLayout: formData.sectionLayout, onePage: formData.onePage, isPaid: false });
      setPdfPreview(url);
    } catch {}
    setIsGeneratingPreview(false);
  };

  // ── Work helpers ────────────────────────────────────────────────────────
  const updateWork = (id, f, v) => setFormData(p => ({ ...p, workExperience: p.workExperience.map(e => e.id === id ? { ...e, [f]: v } : e) }));
  const addWork    = () => setFormData(p => ({ ...p, workExperience: [...p.workExperience, makeWork()] }));
  const removeWork = id => setFormData(p => ({ ...p, workExperience: p.workExperience.filter(e => e.id !== id) }));
  const addBullet  = expId => setFormData(p => ({ ...p, workExperience: p.workExperience.map(e => e.id === expId ? { ...e, responsibilities: [...e.responsibilities, ""] } : e) }));
  const updateBullet = (expId, idx, val) => setFormData(p => ({ ...p, workExperience: p.workExperience.map(e => e.id === expId ? { ...e, responsibilities: e.responsibilities.map((r, i) => i === idx ? val : r) } : e) }));
  const removeBullet = (expId, idx) => setFormData(p => ({ ...p, workExperience: p.workExperience.map(e => e.id === expId ? { ...e, responsibilities: e.responsibilities.filter((_, i) => i !== idx) } : e) }));

  const generateAIBullets = async expId => {
    const exp = formData.workExperience.find(e => e.id === expId);
    if (!exp?.position || !exp?.company) { showToast("Enter job title and company first", "warning"); return; }
    setIsGeneratingBullets(expId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/generate-responsibilities`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ position: exp.position, company: exp.company, jobDescription: formData.jobDescription }),
      });
      const data = await res.json();
      if (data.success && data.responsibilities) {
        setFormData(p => ({ ...p, workExperience: p.workExperience.map(e => e.id === expId ? { ...e, responsibilities: data.responsibilities } : e) }));
        showToast("AI bullets generated!");
      } else throw new Error(data.detail || "Failed");
    } catch (err) { showToast(err.message || "Failed to generate bullets", "danger"); }
    finally { setIsGeneratingBullets(null); }
  };

  // ── Education helpers ───────────────────────────────────────────────────
  const updateEdu = (id, f, v) => setFormData(p => ({ ...p, education: p.education.map(e => e.id === id ? { ...e, [f]: v } : e) }));
  const addEdu    = () => setFormData(p => ({ ...p, education: [...p.education, makeEdu()] }));
  const removeEdu = id => setFormData(p => ({ ...p, education: p.education.filter(e => e.id !== id) }));

  // ── Skills helpers ──────────────────────────────────────────────────────
  const updateSkill = (idx, val) => setFormData(p => ({ ...p, skills: p.skills.map((s, i) => i === idx ? val : s) }));
  const addSkill    = () => setFormData(p => ({ ...p, skills: [...p.skills, ""] }));
  const removeSkill = idx => setFormData(p => ({ ...p, skills: p.skills.filter((_, i) => i !== idx) }));

  // ── AI generation ───────────────────────────────────────────────────────
  const generateResume = async () => {
    if (!formData.personalInfo.fullName) { showToast("Enter your name in Step 1 first", "warning"); return; }
    if (!formData.jobDescription) { showToast("Add a job description in Step 5 first", "warning"); return; }
    setIsGenerating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/generate-resume`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          personalInfo: formData.personalInfo,
          workExperience: formData.workExperience.filter(e => e.company),
          education: formData.education.filter(e => e.institution),
          skills: formData.skills.filter(Boolean),
          targetJobTitle: formData.targetJobTitle,
          jobDescription: formData.jobDescription,
        }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Generation failed"); }
      const result = await res.json();
      setGeneratedResume({ ...result, generatedAt: Date.now() });
      showToast("Resume generated! Review and download below.");
    } catch (err) { showToast(err.message || "Failed to generate resume. Please try again.", "danger"); }
    finally { setIsGenerating(false); }
  };

  // Job-posting URL: validated and extracted automatically as the user types
  // or pastes (debounced) — no Extract button. Returns a normalized href or
  // null when the value isn't a usable http(s) URL yet.
  const normalizeJobUrl = (v) => {
    const s = (v || "").trim();
    if (!s || /\s/.test(s)) return null;
    const candidate = /^https?:\/\//i.test(s) ? s : `https://${s}`;
    try {
      const u = new URL(candidate);
      if (!/^https?:$/.test(u.protocol)) return null;
      // Require a real dotted hostname with a plausible TLD
      if (!/^[^.]+(\.[^.]+)+$/.test(u.hostname) || !/\.[a-z]{2,}$/i.test(u.hostname)) return null;
      return u.href;
    } catch { return null; }
  };

  const lastScrapedUrl = useRef(null);
  useEffect(() => {
    const raw = (formData.jobUrl || "").trim();
    if (!raw) { setJobUrlError(""); return undefined; }
    const t = setTimeout(async () => {
      const normalized = normalizeJobUrl(raw);
      if (!normalized) { setJobUrlError("Enter a valid URL, e.g. https://company.com/jobs/123"); return; }
      setJobUrlError("");
      if (normalized === lastScrapedUrl.current) return; // already extracted this one
      lastScrapedUrl.current = normalized;
      setIsScrapingJob(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/scrape-job`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ url: normalized }) });
        if (!res.ok) throw new Error("Failed to extract job posting");
        const data = await res.json();
        setField("jobDescription", data.jobDescription);
        showToast("Job description extracted!");
      } catch (err) {
        lastScrapedUrl.current = null; // editing the URL again retries
        showToast(err.message || "Failed to extract. Paste the description manually.", "danger");
      } finally { setIsScrapingJob(false); }
    }, 800);
    return () => clearTimeout(t);
  }, [formData.jobUrl]); // eslint-disable-line

  // ── Download handlers ───────────────────────────────────────────────────
  // Rides the same /payment-success animation page a purchase does.
  const handleSubscriptionDownload = () => {
    if (!generatedResume) { showToast("Generate your resume first", "warning"); return; }
    const token = localStorage.getItem("userToken");
    if (!token && !isFree) { showToast("Please log in to use your subscription", "warning"); return; }
    localStorage.setItem("pendingResumeData", JSON.stringify({ generatedResume, formData, selectedTemplate: formData.template }));
    localStorage.setItem("pendingSubTemplate", formData.template || "");
    localStorage.setItem("pendingSubAmount", String(ownDesign ? 0 : RESUME_PRICE)); // the wallet pays this exact price
    localStorage.removeItem("pendingCustomerEmail");
    localStorage.setItem("pendingResetKeys", JSON.stringify([STORAGE_KEY, GENERATED_KEY]));
    navigate(`/payment-success?type=ai-resume&source=app&${isFree ? "free=1" : "sub=1"}`);
  };

  // ── Coupon ──
  // Auto-apply: as the user types or pastes a code we look it up (debounced)
  // and apply it with a toast — no Apply button.
  const RESUME_PRICE = useDocPrice("ai-resume", 9.99); // the admin's price, else 9.99; zero is free for anyone
  // the account balance pays this document's exact price when no plan or
  // gifted downloads cover it
  const walletCovers = !ownDesign && !hasActiveSubscription && walletBalance(user) >= RESUME_PRICE;
  const isFree = !ownDesign && RESUME_PRICE === 0;
  const covered = ownDesign || isFree || hasActiveSubscription || walletCovers;
  useEffect(() => {
    const code = couponCode.trim();
    if (!code || appliedDiscount) { setIsValidatingCoupon(false); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setIsValidatingCoupon(true);
      try {
        const { ok, data } = await nativePost(`${BACKEND_URL}/api/validate-coupon`, { code, generatorType: "ai-resume" });
        if (cancelled) return;
        if (ok && data?.valid) {
          const discountAmount = RESUME_PRICE * data.discountPercent / 100;
          setAppliedDiscount({ code: data.code, discountPercent: data.discountPercent, discountedPrice: parseFloat((RESUME_PRICE - discountAmount).toFixed(2)) });
          setCouponError("");
          showToast(`Coupon ${data.code} applied: ${data.discountPercent}% off!`);
        } else {
          setCouponError(data?.detail || "Invalid coupon code");
          setAppliedDiscount(null);
        }
      } catch { if (!cancelled) { setCouponError("Connection error. Please try again."); setAppliedDiscount(null); } }
      finally { if (!cancelled) setIsValidatingCoupon(false); }
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
  }, [couponCode]); // eslint-disable-line
  const removeCoupon = () => { setCouponCode(""); setAppliedDiscount(null); setCouponError(""); };
  const finalPrice = appliedDiscount ? appliedDiscount.discountedPrice : RESUME_PRICE;

  const renderCouponBlock = () => (
    <div>
      {!appliedDiscount ? (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <IonInput
              fill="outline" labelPlacement="floating" label="Coupon code"
              value={couponCode}
              onIonInput={e => { setCouponCode((e.detail.value || "").toUpperCase()); setCouponError(""); }}
              style={{ flex: 1, fontFamily: "monospace" }}
            />
            {isValidatingCoupon && <IonSpinner name="crescent" style={{ flexShrink: 0 }} />}
          </div>
          {couponError && <IonNote color="danger" style={{ display: "block", marginTop: 4, fontSize: "0.75rem" }}>{couponError}</IonNote>}
        </>
      ) : (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", background: "var(--ion-color-success)", borderRadius: 6 }}>
          <span style={{ color: "var(--ion-color-success-contrast)", fontWeight: 600, fontSize: "0.85rem" }}>
            {appliedDiscount.code}: {appliedDiscount.discountPercent}% off
          </span>
          <IonButton fill="clear" size="small" onClick={removeCoupon} style={{ "--color": "var(--ion-color-success-contrast)" }}>
            <IonIcon slot="icon-only" icon={closeOutline} />
          </IonButton>
        </div>
      )}
    </div>
  );

  // One-time purchase: open the in-app card checkout
  const handleStripeCheckout = () => {
    if (!generatedResume) { showToast("Generate your resume first", "warning"); return; }
    setPaymentOpen(true);
  };

  // Runs after the card payment succeeds — store the pending form data (same
  // keys the hosted-checkout flow used) and hand off to /payment-success,
  // which generates + downloads + emails and shows the success screen. The
  // webhook records the purchase from the payment-intent metadata.
  const handlePaymentSuccess = ({ email, paymentIntentId }) => {
    localStorage.setItem("pendingResumeData", JSON.stringify({ generatedResume, formData, selectedTemplate: formData.template }));
    localStorage.setItem("pendingCustomerEmail", email);
    // Reset this form only after the payment succeeds and the file downloads.
    localStorage.setItem("pendingResetKeys", JSON.stringify([STORAGE_KEY, GENERATED_KEY]));
    navigate(`/payment-success?type=ai-resume&source=app&payment_intent=${encodeURIComponent(paymentIntentId)}`);
  };

  // ── Helper components ───────────────────────────────────────────────────
  const Field = ({ label, value, onChange, type = "text", placeholder = "" }) => (
    <IonInput value={value} onIonInput={e => onChange(e.detail.value)} type={type} placeholder={placeholder}
      fill="outline" labelPlacement="floating" label={label} style={inputStyle} />
  );

  // ── Step renderers ────────────────────────────────────────────────────

  const renderStep1 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Upload an existing resume to auto-fill every step */}
      <div>
        <span style={labelStyle}>Have a resume already?</span>
        {uploadedResumeName && !isParsingResume ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.35)" }}>
            <IonIcon icon={documentTextOutline} style={{ fontSize: 18, color: "#34d399", flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--ion-text-color)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{uploadedResumeName}</div>
              <div style={{ fontSize: "0.72rem", color: "var(--ion-color-medium)" }}>Parsed. Your details were filled in below</div>
            </div>
            <IonButton fill="clear" size="small" onClick={() => resumeFileRef.current?.click()} style={{ flexShrink: 0 }}>Replace</IonButton>
          </div>
        ) : (
          isParsingResume ? (
            <div style={{ padding: 14, borderRadius: 8, border: "2px dashed var(--ion-color-medium)", textAlign: "center", color: "var(--ion-color-medium)", fontSize: "0.82rem" }}>
              <IonSpinner name="crescent" style={{ width: 18, height: 18, verticalAlign: "middle", marginRight: 8 }} />Parsing your resume…
            </div>
          ) : (
            <UploadDropzone
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onFile={(f) => handleResumeUpload({ target: { files: [f], value: "" } })}
            />
          )
        )}
        <input ref={resumeFileRef} type="file" style={{ display: "none" }}
          accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={handleResumeUpload} />
      </div>

      <div style={headingStyle}>Personal Information</div>
      <Field label="Full Name *" value={formData.personalInfo.fullName} onChange={v => setFormData(p => ({ ...p, personalInfo: { ...p.personalInfo, fullName: v } }))} />
      <Field label="Email" value={formData.personalInfo.email} onChange={v => setFormData(p => ({ ...p, personalInfo: { ...p.personalInfo, email: v } }))} type="email" />
      <Field label="Phone" value={formData.personalInfo.phone} onChange={v => setFormData(p => ({ ...p, personalInfo: { ...p.personalInfo, phone: v } }))} type="tel" />
      <Field label="Location (City, State)" value={formData.personalInfo.location} onChange={v => setFormData(p => ({ ...p, personalInfo: { ...p.personalInfo, location: v } }))} />
      <Field label="LinkedIn URL" value={formData.personalInfo.linkedin} onChange={v => setFormData(p => ({ ...p, personalInfo: { ...p.personalInfo, linkedin: v } }))} />
      <Field label="Portfolio / Website" value={formData.personalInfo.website} onChange={v => setFormData(p => ({ ...p, personalInfo: { ...p.personalInfo, website: v } }))} />
    </div>
  );

  const renderStep2 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={headingStyle}>Work Experience</div>
      <div>
        {formData.workExperience.map((exp, idx) => (
          <div key={exp.id} className="period-card">
            <div className="admin-subbar sheet-card-head period-head with-action">
              <span>Job {idx + 1}{exp.company ? `: ${exp.company}` : ""}</span>
              {formData.workExperience.length > 1 && (
                <IonButton fill="clear" color="danger" size="small" className="period-head-remove" onClick={() => removeWork(exp.id)} aria-label="Remove job">
                  <IonIcon slot="icon-only" icon={trashOutline} />
                </IonButton>
              )}
            </div>
            <div className="period-form">
              <IonInput {...FLOAT} label="Job Title" value={exp.position} onIonInput={e => updateWork(exp.id, "position", e.detail.value)} />
              <IonInput {...FLOAT} label="Company" value={exp.company} onIonInput={e => updateWork(exp.id, "company", e.detail.value)} />
              <IonInput {...FLOAT} className="admin-field period-wide" label="Location" value={exp.location} onIonInput={e => updateWork(exp.id, "location", e.detail.value)} />
              <IonInput {...FLOAT} label="Start Date" type="month" value={exp.startDate} onIonInput={e => updateWork(exp.id, "startDate", e.detail.value)} />
              {!exp.current && <IonInput {...FLOAT} label="End Date" type="month" value={exp.endDate} onIonInput={e => updateWork(exp.id, "endDate", e.detail.value)} />}
              <IonItem lines="none" className="admin-field period-wide period-toggle">
                <IonLabel style={{ fontSize: "0.85rem" }}>Currently working here</IonLabel>
                <IonToggle slot="end" checked={exp.current} onIonChange={e => updateWork(exp.id, "current", e.detail.checked)} />
              </IonItem>
            </div>
            <div className="admin-subbar sheet-card-head period-head with-action">
              <span>Responsibilities / Bullets</span>
              <IonButton color="tertiary" size="small" className="admin-search-subbar-btn" onClick={() => generateAIBullets(exp.id)} disabled={isGeneratingBullets === exp.id}>
                {isGeneratingBullets === exp.id
                  ? <IonSpinner name="crescent" style={{ width: 14, height: 14 }} />
                  : <><IonIcon icon={sparklesOutline} slot="start" style={{ fontSize: 12 }} />AI Generate</>}
              </IonButton>
            </div>
            <div className="period-form">
              {exp.responsibilities.map((r, rIdx) => (
                <div key={rIdx} className="period-wide period-row">
                  <IonInput {...FLOAT} label={`Bullet ${rIdx + 1}`} value={r} onIonInput={e => updateBullet(exp.id, rIdx, e.detail.value)} />
                  {exp.responsibilities.length > 1 && (
                    <IonButton fill="clear" size="small" color="danger" className="period-row-remove" onClick={() => removeBullet(exp.id, rIdx)} aria-label="Remove bullet">
                      <IonIcon slot="icon-only" icon={trashOutline} />
                    </IonButton>
                  )}
                </div>
              ))}
              <div className="period-wide">
                <IonButton color="light" size="small" onClick={() => addBullet(exp.id)}>
                  <IonIcon icon={addOutline} slot="start" />Add Bullet
                </IonButton>
              </div>
            </div>
          </div>
        ))}
      </div>
      <IonButton color="light" expand="block" onClick={addWork}>
        <IonIcon icon={addOutline} slot="start" />Add Job
      </IonButton>
    </div>
  );

  const renderStep3 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={headingStyle}>Education</div>
      <div>
        {formData.education.map((edu, idx) => (
          <div key={edu.id} className="period-card">
            <div className="admin-subbar sheet-card-head period-head with-action">
              <span>School {idx + 1}{edu.institution ? `: ${edu.institution}` : ""}</span>
              {formData.education.length > 1 && (
                <IonButton fill="clear" color="danger" size="small" className="period-head-remove" onClick={() => removeEdu(edu.id)} aria-label="Remove education">
                  <IonIcon slot="icon-only" icon={trashOutline} />
                </IonButton>
              )}
            </div>
            <div className="period-form">
              <IonInput {...FLOAT} className="admin-field period-wide" label="Institution" value={edu.institution} onIonInput={e => updateEdu(edu.id, "institution", e.detail.value)} />
              <IonInput {...FLOAT} label="Degree" placeholder="B.S., M.A." value={edu.degree} onIonInput={e => updateEdu(edu.id, "degree", e.detail.value)} />
              <IonInput {...FLOAT} label="Field of Study" value={edu.field} onIonInput={e => updateEdu(edu.id, "field", e.detail.value)} />
              <IonInput {...FLOAT} label="Graduation Date" type="month" value={edu.graduationDate} onIonInput={e => updateEdu(edu.id, "graduationDate", e.detail.value)} />
              <IonInput {...FLOAT} label="GPA (Optional)" placeholder="3.8" value={edu.gpa} onIonInput={e => updateEdu(edu.id, "gpa", e.detail.value)} />
            </div>
          </div>
        ))}
      </div>
      <IonButton color="light" expand="block" onClick={addEdu}>
        <IonIcon icon={addOutline} slot="start" />Add Education
      </IonButton>
    </div>
  );

  const renderStep4 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={headingStyle}>Skills</div>
      <div className="period-card">
        <div className="admin-subbar sheet-card-head period-head">
          <span>Technical skills, tools, languages and certifications</span>
        </div>
        <div className="period-form">
          {formData.skills.map((skill, idx) => (
            <div key={idx} className="period-row">
              <IonInput {...FLOAT} label={`Skill ${idx + 1}`} value={skill} onIonInput={e => updateSkill(idx, e.detail.value)} />
              {formData.skills.length > 1 && (
                <IonButton fill="clear" size="small" color="danger" className="period-row-remove" onClick={() => removeSkill(idx)} aria-label="Remove skill">
                  <IonIcon slot="icon-only" icon={trashOutline} />
                </IonButton>
              )}
            </div>
          ))}
        </div>
      </div>
      <IonButton color="light" expand="block" onClick={addSkill}>
        <IonIcon icon={addOutline} slot="start" />Add Skill
      </IonButton>
    </div>
  );

  const renderStep5 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={headingStyle}>Target Job</div>
      <Field label="Target Job Title" value={formData.targetJobTitle} onChange={v => setField("targetJobTitle", v)} placeholder="e.g. Senior Software Engineer" />
      {/* Paste or type a job URL — validated and extracted automatically */}
      <div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <IonInput value={formData.jobUrl} onIonInput={e => setField("jobUrl", e.detail.value)}
            type="url" inputmode="url" placeholder="https://company.com/jobs/123"
            fill="outline" labelPlacement="floating" label="Job Posting URL (Optional)" style={{ ...inputStyle, flex: 1 }} />
          {isScrapingJob && <IonSpinner name="crescent" style={{ width: 20, height: 20, flexShrink: 0 }} />}
        </div>
        {jobUrlError && (
          <IonNote color="danger" style={{ display: "block", marginTop: 4, fontSize: "0.75rem" }}>{jobUrlError}</IonNote>
        )}
        {isScrapingJob && (
          <IonNote color="medium" style={{ display: "block", marginTop: 4, fontSize: "0.75rem" }}>Extracting job description…</IonNote>
        )}
      </div>
      <IonTextarea value={formData.jobDescription} onIonInput={e => setField("jobDescription", e.detail.value)}
        rows={10} fill="outline" labelPlacement="floating" label="Job Description *"
        placeholder="Paste the full job description and the AI tailors your resume to it" style={inputStyle} />
    </div>
  );

  const renderStep6 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={headingStyle}>Style &amp; Generate</div>
      <IonSelect {...FLOAT} interface="popover" label="Template" value={formData.template} onIonChange={e => setField("template", e.detail.value)}>
        {templateOptions.map(t => <IonSelectOption key={t.value} value={t.value}>{t.label}</IonSelectOption>)}
      </IonSelect>
      <IonSelect {...FLOAT} interface="popover" label="Font" value={formData.font} onIonChange={e => setField("font", e.detail.value)}>
        {FONTS.map(f => <IonSelectOption key={f.value} value={f.value}>{f.label}</IonSelectOption>)}
      </IonSelect>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <IonCheckbox checked={formData.onePage} onIonChange={e => setField("onePage", e.detail.checked)} />
        <span style={{ fontSize: "0.88rem", color: "var(--ion-text-color)" }}>Fit to one page</span>
      </div>
      <IonButton expand="block" color="tertiary" onClick={generateResume} disabled={isGenerating}>
        {isGenerating
          ? <><IonSpinner name="crescent" slot="start" />Generating with AI...</>
          : <><IonIcon icon={sparklesOutline} slot="start" />Generate Resume</>}
      </IonButton>
      {generatedResume && (
        <>
          <div style={{ padding: 14, borderRadius: 8, background: "var(--ion-color-success)", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--ion-color-success-contrast)", flexShrink: 0 }} />
            <span style={{ fontSize: "0.85rem", color: "var(--ion-color-success-contrast)", fontWeight: 500, flex: 1 }}>
              {(() => {
                const ts = generatedResume.generatedAt;
                if (!ts) return "Resume generated and ready to download";
                const d = new Date(ts);
                const sameDay = d.toDateString() === new Date().toDateString();
                const when = sameDay
                  ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
                  : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
                return `Resume generated at ${when}, ready to download`;
              })()}
            </span>
            <IonButton fill="clear" size="small" onClick={generateResume} style={{ "--color": "var(--ion-color-success-contrast)", flexShrink: 0 }}>
              <IonIcon icon={refreshOutline} slot="icon-only" style={{ fontSize: 16 }} />
            </IonButton>
          </div>
          <IonButton expand="block" color="light" onClick={() => setShowPreview(true)} disabled={isGeneratingPreview}>
            {isGeneratingPreview ? <IonSpinner name="crescent" slot="start" style={{ width: 16, height: 16 }} /> : <IonIcon icon={eyeOutline} slot="start" />}
            Preview
          </IonButton>
          {covered ? (
            <IonButton expand="block" onClick={handleSubscriptionDownload} disabled={isProcessing}
              style={{ "--background": "var(--ion-color-primary)", "--background-activated": "var(--ion-color-primary-shade)", "--color": "var(--ion-color-primary-contrast)" }}>
              {isProcessing ? <IonSpinner name="crescent" style={{ color: "#fff" }} /> : <><IonIcon icon={cloudDownloadOutline} slot="start" />{walletCovers ? "Download (Balance)" : "Download (Subscription)"}</>}
            </IonButton>
          ) : (
            <>
              {renderCouponBlock()}
              <IonButton expand="block" onClick={handleStripeCheckout} disabled={isProcessing}
                style={{ "--background": "var(--ion-color-primary)", "--background-activated": "var(--ion-color-primary-shade)", "--color": "var(--ion-color-primary-contrast)" }}>
                {isProcessing ? <IonSpinner name="crescent" style={{ color: "#fff" }} /> : <><IonIcon icon={cloudDownloadOutline} slot="start" />Buy &amp; Download (${finalPrice.toFixed(2)})</>}
              </IonButton>
            </>
          )}
        </>
      )}
    </div>
  );

  const stepContent = [renderStep1, renderStep2, renderStep3, renderStep4, renderStep5, renderStep6];

  if (!isOpen && !embed) return null;
  const isMobile = window.innerWidth < 768;

  return createPortal(
    <div className="modal-backdrop" style={{ position: "fixed", inset: 0, zIndex: 10000, background: isMobile ? "var(--ion-background-color, #f2f2f7)" : "rgba(0,0,0,0.5)", display: "flex", alignItems: isMobile ? "stretch" : "center", justifyContent: isMobile ? "stretch" : "center" }}>
      {/* the pay stub modal's footprint on every tab: 600 wide and 90vh tall on desktop, full screen on a phone */}
      <div className="modal-slide-up" style={{ background: "var(--ion-background-color, #f2f2f7)", color: "var(--ion-text-color)", display: "flex", flexDirection: "column", width: "100%", maxWidth: isMobile ? "100%" : 600, height: isMobile ? "100%" : "90vh", maxHeight: isMobile ? "100%" : "90vh", overflow: "hidden" }}>

        <IonHeader>
          <IonToolbar style={{ "--background": "var(--ion-card-background)", "--color": "var(--ion-text-color)" }}>
            <IonButtons slot="start">
              <IonButton fill="clear" shape="round" onClick={onClose}>
                <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "1.8em", color: "var(--ion-text-color)" }}>
                  <IonIcon icon={closeOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
                </span>
              </IonButton>
            </IonButtons>
            <IonTitle style={{ fontWeight: 700 }}>AI Resume Builder</IonTitle>
          </IonToolbar>
        </IonHeader>

        {/* Step tabs */}
        <div style={{ background: "var(--ion-card-background)", flexShrink: 0, borderBottom: "1px solid var(--app-divider)" }}>
          <IonSegment scrollable value={String(currentStep)} onIonChange={e => setCurrentStep(Number(e.detail.value))}
            style={{ "--background": "transparent" }}>
            {STEPS.map(step => (
              <IonSegmentButton key={step.id} value={String(step.id)} layout="icon-top"
                style={{ "--color": "var(--ion-color-medium)", "--color-checked": "var(--ion-color-primary)", "--indicator-color": "var(--ion-color-primary)", "--padding-top": "8px", "--padding-bottom": "8px", minWidth: 72, flexShrink: 0 }}>
                <IonIcon icon={step.icon} style={{ fontSize: 17 }} />
                <IonLabel style={{ fontSize: "0.62rem", fontWeight: 600 }}>{step.label}</IonLabel>
              </IonSegmentButton>
            ))}
          </IonSegment>
        </div>

        {/* Step content */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "16px 16px 24px" }}>
          <div style={cardStyle}>
            {stepContent[currentStep - 1]()}
          </div>

          {/* Prev / Next */}
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            {currentStep > 1 && (
              <IonButton color="light" onClick={() => setCurrentStep(s => s - 1)} style={{ flex: 1 }}>
                <IonIcon icon={chevronBackOutline} slot="start" />Back
              </IonButton>
            )}
            {currentStep < 6 && (
              <IonButton color="primary" onClick={() => setCurrentStep(s => s + 1)} style={{ flex: 1 }}>
                Next<IonIcon icon={chevronForwardOutline} slot="end" />
              </IonButton>
            )}
          </div>
        </div>
      </div>

      {/* Preview modal — same layout as the paystub preview: full-screen on
          mobile, centered panel on desktop, download CTA at the bottom */}
      {showPreview && (
        <div className="modal-backdrop" style={{ position: "fixed", inset: 0, zIndex: 10002, background: isMobile ? "var(--ion-background-color, #f2f2f7)" : "rgba(0,0,0,0.5)", display: "flex", alignItems: isMobile ? "stretch" : "center", justifyContent: isMobile ? "stretch" : "center" }}>
          <div className="modal-slide-up" style={{ background: "var(--ion-background-color, #f2f2f7)", color: "var(--ion-text-color)", display: "flex", flexDirection: "column", width: "100%", maxWidth: isMobile ? "100%" : 600, height: isMobile ? "100%" : "auto", maxHeight: isMobile ? "100%" : "90vh", overflow: "hidden" }}>
            <IonHeader>
              <IonToolbar style={{ "--background": "var(--ion-card-background)", "--color": "var(--ion-text-color)" }}>
                <IonButtons slot="start">
                  <IonButton fill="clear" shape="round" onClick={() => setShowPreview(false)}>
                    <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "1.8em", color: "var(--ion-text-color)" }}>
                      <IonIcon icon={closeOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
                    </span>
                  </IonButton>
                </IonButtons>
                <IonTitle style={{ fontWeight: 700 }}>Preview</IonTitle>
              </IonToolbar>
            </IonHeader>
            <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
              {isGeneratingPreview ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 320, background: "var(--ion-color-step-100)", borderRadius: 8 }}>
                  <IonSpinner name="crescent" style={{ marginBottom: 8 }} />
                  <span style={{ fontSize: "0.8rem", color: "var(--ion-color-medium)" }}>Generating preview…</span>
                </div>
              ) : pdfPreview ? (
                <div style={{ borderRadius: 8, overflow: "hidden", border: "1px solid var(--ion-color-light-shade)", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                  <img src={pdfPreview} alt="Resume preview" style={{ width: "100%", display: "block" }} />
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 320, background: "var(--ion-color-step-100)", borderRadius: 8, border: "2px dashed var(--ion-color-light-shade)" }}>
                  <IonIcon icon={eyeOutline} style={{ fontSize: "2.5rem", color: "var(--ion-color-medium)", marginBottom: 8 }} />
                  <p style={{ fontSize: "0.8rem", color: "var(--ion-color-medium)", textAlign: "center", margin: 0 }}>No preview available yet</p>
                </div>
              )}
              {covered ? (
                <IonButton expand="block" onClick={handleSubscriptionDownload} disabled={isProcessing}
                  style={{ marginTop: 20, "--background": "var(--ion-color-primary)", "--background-activated": "var(--ion-color-primary-shade)", "--color": "var(--ion-color-primary-contrast)" }}>
                  {isProcessing ? <IonSpinner name="crescent" style={{ color: "#fff" }} /> : <><IonIcon icon={cloudDownloadOutline} slot="start" />{walletCovers ? "Download (Balance)" : "Download (Subscription)"}</>}
                </IonButton>
              ) : (
                <>
                  <div style={{ marginTop: 20 }}>{renderCouponBlock()}</div>
                  <IonButton expand="block" onClick={handleStripeCheckout} disabled={isProcessing}
                    style={{ marginTop: 12, "--background": "var(--ion-color-primary)", "--background-activated": "var(--ion-color-primary-shade)", "--color": "var(--ion-color-primary-contrast)" }}>
                    {isProcessing ? <IonSpinner name="crescent" style={{ color: "#fff" }} /> : <><IonIcon icon={cloudDownloadOutline} slot="start" />Buy &amp; Download (${finalPrice.toFixed(2)})</>}
                  </IonButton>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {paymentOpen && (
        <CheckoutWithPaywall
          docLabel="AI Resume"
          documentType="ai-resume"
          template={formData.template}
          basePrice={RESUME_PRICE}
          discount={appliedDiscount}
          previewImage={pdfPreview || null}
          onSuccess={handlePaymentSuccess}
          onClose={() => setPaymentOpen(false)}
        />
      )}

      <FreshOverlay as={IonToast}
        buttons={TOAST_CLOSE_BUTTONS} isOpen={toastOpen} onDidDismiss={() => setToastOpen(false)}
        message={toastMessage} duration={3500} position="top" color={toastColor} />
    </div>,
    document.querySelector("ion-app") || document.body
  );
}
